import { init, replayIntegration, setTag, thirdPartyErrorFilterIntegration } from '@sentry/react';
import type { ErrorEvent } from '@sentry/react';
import { FeatureToggle, isFeatureEnabled } from '../../../assets/js/util/featureToggle';
import ignoreErrors from '../../../assets/js/modules/shared/sentry/ignoreErrors';
import denyUrls from '../../../assets/js/modules/shared/sentry/denyUrls';
import SentryErrorFilter from '../../../assets/js/modules/shared/sentry/SentryErrorFilter';

const filter = new SentryErrorFilter();

export default class Sentry {
    static #instance: Sentry | null = null;

    public static getInstance(): Sentry {
        if (this.#instance === null) {
            this.#instance = new Sentry();
        }

        return this.#instance;
    }

    public static destroyInstance(): void {
        this.#instance = null;
    }

    public constructor() {
        if (!process.env.SENTRY_DSN_REACT) {
            return;
        }

        init({
            dsn: process.env.SENTRY_DSN_REACT,
            beforeSend: (event: ErrorEvent): ErrorEvent | PromiseLike<ErrorEvent | null> | null => {
                // Want to test beforeSend in development? Comment out the thirdPartyErrorFilterIntegration
                if (filter.shouldIgnore(event)) return null;
                return event;
            },
            allowUrls: [/https:\/\/assets\.nature\.house\/build-react\//],
            denyUrls,
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 0.1,
            integrations: [
                replayIntegration(),
                ...(process.env.SENTRY_APP_KEY && process.env.SENTRY_APP_KEY !== ''
                    ? [
                          thirdPartyErrorFilterIntegration({
                              filterKeys: [process.env.SENTRY_APP_KEY],
                              behaviour: 'drop-error-if-exclusively-contains-third-party-frames'
                          })
                      ]
                    : [])
            ],
            ignoreErrors
        });

        const isLandlord = document.body.classList.contains('userIsLandlord');
        setTag('is-landlord', isLandlord.toString());

        const env = document.documentElement.dataset.deploymentEnv;
        if (env) {
            setTag('deployment-environment', env);
        }

        const featuresEnabled = Object.values(FeatureToggle)
            .map((feature): string => (isFeatureEnabled(feature) ? feature : ''))
            .filter(Boolean);

        if (featuresEnabled.length) {
            setTag('features-enabled', featuresEnabled.join(','));
        }
    }
}
